import React from "react";
import { motion } from "framer-motion";
import { Truck, AppWindow, Building2, HardHat, Store, Warehouse, ArrowRight, CheckCircle2, Zap } from "lucide-react";
import { AnimatedLines } from "@/components/landing/AnimatedLines";

const OFFERS = [
  {
    icon: Truck,
    title: "Nettoyage de flotte",
    desc: "Entretien et detailing de vos véhicules d'entreprise : utilitaires, VL, poids lourds. Image de marque impeccable sur la route.",
    image: "https://static.prod-images.emergentagent.com/jobs/877c6a23-ea34-4c44-bcad-04f4869b2517/images/c92ca5ca987d3eac3ff8a68eaa4332295544be018956116f8b0e61e15cb0a301.png",
    testid: "pro-fleet",
  },
  {
    icon: AppWindow,
    title: "Nettoyage de vitres",
    desc: "Vitrines, baies et surfaces vitrées sans une trace, même en hauteur ou en accès difficile.",
    highlight: "Jusqu'à 3 étages sans nacelle grâce à notre perche télescopique : plus économique et plus flexible.",
    image: "https://static.prod-images.emergentagent.com/jobs/877c6a23-ea34-4c44-bcad-04f4869b2517/images/3c9cdc1ac511b2ad87a1d82621a21dca0e3adbfba35ec8655b407af2e1f71f53.png",
    testid: "pro-windows",
  },
  {
    icon: Building2,
    title: "Immeubles & copropriétés",
    desc: "Contrats de nettoyage régulier des parties communes, façades et extérieurs. Un patrimoine entretenu, une valeur préservée.",
    image: "https://static.prod-images.emergentagent.com/jobs/877c6a23-ea34-4c44-bcad-04f4869b2517/images/171992633488fbc6efa1b6bc08607f1deb72ccef91e85e6a804c7b1b34b3453a.png",
    testid: "pro-buildings",
  },
  {
    icon: HardHat,
    title: "Nettoyage de fin de chantier",
    desc: "Après travaux ou construction neuve : dépoussiérage complet, retrait des résidus (plâtre, peinture, colle) et remise en état pour une livraison impeccable.",
    image: "https://static.prod-images.emergentagent.com/jobs/877c6a23-ea34-4c44-bcad-04f4869b2517/images/f6fe8ca1200f630e318ce9f793cfb9d03821b4b095ee5a5601d060f426463259.png",
    testid: "pro-endsite",
  },
  {
    icon: Store,
    title: "Surfaces de vente",
    desc: "Magasins, showrooms et commerces : sols, vitrines et espaces d'accueil impeccables pour valoriser votre point de vente.",
    image: "https://static.prod-images.emergentagent.com/jobs/877c6a23-ea34-4c44-bcad-04f4869b2517/images/37cc3d82bdfd6e70284be6d0c68562450b49f26b6def3a87e6d6ca912e7d3402.png",
    testid: "pro-retail",
  },
  {
    icon: Warehouse,
    title: "Entrepôts & logistique",
    desc: "Nettoyage et dépoussiérage de grandes surfaces industrielles : sols béton, zones de stockage et quais, pour un environnement de travail sain.",
    image: "https://static.prod-images.emergentagent.com/jobs/877c6a23-ea34-4c44-bcad-04f4869b2517/images/33e8081a4af2153705567056e14d314da451692315c8c70e2375cb7f5c589a7c.png",
    testid: "pro-warehouse",
  },
];

const CARD_HIDDEN = { opacity: 0, y: 30 };
const CARD_SHOW = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, margin: "-60px" };
const CARD_HOVER = { y: -6 };

export const ProSection = ({ onReserve }) => {
  return (
    <section
      id="pros"
      data-testid="pro-section"
      className="relative grain overflow-hidden bg-ink text-white py-20 md:py-28 scroll-mt-44"
    >
      <AnimatedLines variant="dark" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white/50">
              <Building2 className="w-4 h-4" /> Offre Professionnels
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05]">
              Une solution dédiée aux entreprises
            </h2>
            <p className="text-white/70 mt-5 text-base sm:text-lg leading-relaxed">
              Devis sur-mesure, planning adapté à votre activité et facturation
              professionnelle. Nous accompagnons entreprises, syndics et collectivités
              de la zone {""}
              <span className="text-white font-semibold">Belfort / Montbéliard / Héricourt</span>.
            </p>
          </div>
          <button
            data-testid="pro-cta"
            onClick={() => onReserve("pro")}
            className="cta shrink-0 inline-flex items-center gap-2 bg-white text-ink font-bold px-7 py-4 rounded-sm hover:bg-marble-dark"
          >
            Demander un devis pro
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {OFFERS.map((o, i) => (
            <motion.div
              key={o.testid}
              data-testid={o.testid}
              initial={CARD_HIDDEN}
              whileInView={CARD_SHOW}
              viewport={CARD_VIEWPORT}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              whileHover={CARD_HOVER}
              className="group flex flex-col bg-ink-light border border-white/10 rounded-sm overflow-hidden hover:border-white/40 transition-colors"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={o.image}
                  alt={o.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-light via-ink-light/30 to-transparent" />
                <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider font-bold text-white bg-black/50 backdrop-blur-sm px-2 py-0.5 rounded-sm">
                  Avant intervention
                </span>
                <span className="absolute -bottom-6 left-6 w-12 h-12 grid place-items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-sm">
                  <o.icon className="w-6 h-6 text-white" />
                </span>
              </div>
              <div className="p-7 pt-10 flex flex-col flex-1">
                <h3 className="font-heading font-bold text-xl">{o.title}</h3>
                <p className="text-white/60 text-sm mt-3 leading-relaxed">{o.desc}</p>
                {o.highlight && (
                  <div className="mt-4 flex items-start gap-2 text-sm font-semibold text-white bg-white/5 border border-white/10 rounded-sm p-3">
                    <Zap className="w-4 h-4 shrink-0 mt-0.5" /> {o.highlight}
                  </div>
                )}
                <div className="mt-auto pt-6 flex items-center gap-2 text-sm font-semibold text-white/70">
                  <CheckCircle2 className="w-4 h-4" /> Contrat récurrent possible
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
